/**
 * What each plan allows. One definition, used by the server to enforce and by
 * the client to explain — so a limit and the sentence describing it can never
 * drift apart.
 *
 * The shape of the split matters as much as the numbers. Limits bind on the
 * **host**, never on the guest: a free account joining a Pro host's sketchpad
 * gets the full five-person experience. That way Pro users bring free users in,
 * and free users meet the limit at the moment they try to host their own — the
 * point where they can already see what they are missing.
 *
 * Nothing about drawing itself is gated. Every brush, every shape, the fill and
 * the export are free forever. Paying buys scale (more people, more sketchpads)
 * and the features that actually cost money to run (stored photos).
 */

export const PLAN_FREE = 'free';
export const PLAN_PRO = 'pro';

export const PLANS = {
  [PLAN_FREE]: {
    id: PLAN_FREE,
    name: 'Free',
    /** People who can draw at once in a sketchpad this account hosts. */
    drawers: 2,
    /** Sketchpads this account can own at any one time. */
    pads: 2,
    /** Extra watchers allowed in beyond the drawers. */
    spectators: 0,
    /** Put a password on a sketchpad. */
    padPasswords: false,
    /** Paste, drop or pick photos into a sketchpad. */
    images: false,
    /** Watch a sketchpad being drawn back as a timelapse. */
    replay: false,
    /** Fork any sketchpad into a fresh one you host. */
    copy: false,
  },
  [PLAN_PRO]: {
    id: PLAN_PRO,
    name: 'Pro',
    drawers: 5,
    pads: 200,
    spectators: 15,
    padPasswords: true,
    images: true,
    replay: true,
    copy: true,
  },
};

/** Prose for the upgrade screen, in the order it should be listed. */
export const PLAN_FEATURES = [
  { key: 'drawers', free: '2 people drawing at once', pro: '5 people drawing at once' },
  { key: 'pads', free: '2 saved sketchpads', pro: 'Up to 200 saved sketchpads' },
  { key: 'spectators', free: 'No watchers', pro: 'Up to 15 watchers on top of the drawers' },
  { key: 'padPasswords', free: '—', pro: 'Password-protect a sketchpad' },
  { key: 'images', free: '—', pro: 'Add photos and images' },
  { key: 'replay', free: '—', pro: 'Timelapse replay of how it was drawn' },
  { key: 'copy', free: '—', pro: 'Copy any sketchpad into a new one you host' },
  { key: 'tools', free: 'Every brush, shape and colour', pro: 'Every brush, shape and colour' },
  { key: 'join', free: 'Join anyone else’s sketchpad', pro: 'Join anyone else’s sketchpad' },
];

/**
 * The plan an account is actually on right now.
 *
 * Paid access is a timestamp rather than a flag, so an expired subscription
 * degrades on its own even if a webhook is missed or a payment provider goes
 * quiet. Nothing has to run on a schedule for this to be correct.
 */
export function planOf(user, now = Date.now()) {
  if (!user) return PLAN_FREE;
  if (user.plan !== PLAN_PRO) return PLAN_FREE;
  const until = Number(user.plan_until ?? user.planUntil ?? 0);
  if (!until) return PLAN_FREE;
  return until > now ? PLAN_PRO : PLAN_FREE;
}

/** The limits for an account. Pass a user row or a plan id. */
export function limitsFor(userOrPlan, now = Date.now()) {
  const plan = typeof userOrPlan === 'string' ? userOrPlan : planOf(userOrPlan, now);
  return PLANS[plan] ?? PLANS[PLAN_FREE];
}

/** True when the account may use a named capability. */
export function can(userOrPlan, feature, now = Date.now()) {
  return Boolean(limitsFor(userOrPlan, now)[feature]);
}

/**
 * Wording shown when someone runs into a limit.
 *
 * Kept here beside the limits so the message always states the real number,
 * and always says what to do next rather than only what went wrong.
 */
export const PAYWALL = {
  drawers: (n) => `Sketchpads you host fit ${n} people at once on the free plan. `
    + 'Ink Share Pro raises that to 5.',
  pads: (n) => `You have ${n} saved sketchpads, which is the free limit. `
    + 'Delete one, or go Pro for up to 200.',
  padPasswords: () => 'Passwords on sketchpads are part of Ink Share Pro.',
  images: () => 'Adding photos is part of Ink Share Pro.',
  replay: () => 'Timelapse replay is part of Ink Share Pro.',
  copy: () => 'Copying a sketchpad is part of Ink Share Pro.',
};
